/**
 * Transparency report builder. Shows the latest report's live totals + category split,
 * lets staff regenerate the PDF, and download it. Auto-refreshed nightly by the
 * transparency-cache job (visible on the dashboard).
 */
import { useEffect, useState } from 'react';
import { FileDown, RefreshCw, FileBarChart } from 'lucide-react';
import { api, getAccessToken } from '../lib/apiClient.js';
import { useToast } from '../components/feedback/ToastProvider.jsx';
import { Card, CardHeader } from '../components/ui/Card.jsx';
import { formatINR, formatDate, CATEGORY_META } from '../features/cases/caseMeta.js';
import styles from './Reports.module.css';

export default function Reports() {
  const toast = useToast();
  const [report, setReport] = useState(null);
  const [status, setStatus] = useState('loading');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setStatus('loading');
    try {
      setReport(await api.get('/reports/latest?format=json'));
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  };
  useEffect(() => { load(); }, []);

  const regenerate = async () => {
    setBusy(true);
    try {
      setReport(await api.post('/reports/generate', {}));
      toast.success('Transparency report regenerated.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    const base = import.meta.env.VITE_API_BASE_URL || '/api';
    fetch(`${base}/reports/latest`, { headers: { Authorization: `Bearer ${getAccessToken()}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'transparency-report.pdf';
        a.click();
      })
      .catch(() => toast.error('Download failed.'));
  };

  if (status === 'loading') return <div className={styles.page}><div className={styles.skel} /></div>;
  if (status === 'error') return <div className={styles.page}><p className={styles.error}>Couldn’t load the report. <button onClick={load}>Retry</button></p></div>;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="eyebrow">Reporting</p>
          <h1 className={styles.h1}>Transparency report</h1>
          <p className={styles.lede}>Period {report.period} · generated {formatDate(report.generatedAt)}</p>
        </div>
        <div className={styles.actions}>
          <button className={styles.secondary} onClick={regenerate} disabled={busy}>
            <RefreshCw size={15} /> {busy ? 'Generating…' : 'Regenerate'}
          </button>
          <button className={styles.primary} onClick={download}>
            <FileDown size={16} /> Download PDF
          </button>
        </div>
      </header>

      <div className={styles.totals}>
        <Total label="Total deployed" value={formatINR(report.totals.deployed)} highlight />
        <Total label="Total received" value={formatINR(report.totals.received)} />
        <Total label="Families served" value={report.totals.beneficiaries} />
      </div>

      <Card>
        <CardHeader title="Where the help went" subtitle="By category" action={<FileBarChart size={16} />} />
        <table className={styles.table}>
          <thead><tr><th>Category</th><th className={styles.right}>Cases</th><th className={styles.right}>Deployed</th></tr></thead>
          <tbody>
            {report.totals.byCategory.map((c) => (
              <tr key={c.category}>
                <td>{CATEGORY_META[c.category]?.label || c.category}</td>
                <td className={`${styles.right} mono`}>{c.count}</td>
                <td className={`${styles.right} mono`}>{formatINR(c.amount)}</td>
              </tr>
            ))}
            {report.totals.byCategory.length === 0 && <tr><td colSpan={3} className={styles.empty}>No closed cases yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function Total({ label, value, highlight }) {
  return (
    <div className={`${styles.totalCard} ${highlight ? styles.totalHighlight : ''}`}>
      <span className={styles.totalLabel}>{label}</span>
      <span className={`${styles.totalValue} mono`}>{value}</span>
    </div>
  );
}
